import { useQuery } from '@tanstack/react-query';
import { legacyPermissionsControllerAtMeQueryOptions } from 'korint-sdk';
import { useKorintConfig } from 'korint-sdk/react';
import korintLogo from './korint-square-logo.png';
import { getSuperAdminUrl } from '../../utils/getSuperAdminUrl';
import { isKorintUser } from '../../utils/isKorintUser';
import { cn } from '../../lib/utils';

interface KorintSuperLinkProps {
  subpath: string;
  className?: string;
}

/**
 * KorintSuperLink component that displays the Korint logo as a link to the Super Admin panel.
 * Only visible to users with @korint.io email addresses.
 * Automatically determines the correct Super Admin URL based on the current API environment.
 */
export function KorintSuperLink({ subpath, className }: KorintSuperLinkProps) {
  const { clientOptions } = useKorintConfig();
  const { data: user } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  // Only show for @korint.io users
  if (!isKorintUser(user?.email)) {
    return null;
  }

  // Get API URL from environment variable
  const apiUrl = import.meta.env.VITE_KORINT_API_URL || '';
  const adminUrl = getSuperAdminUrl(apiUrl, subpath, clientOptions.tenant);

  // If localhost or URL parsing failed, remove icon
  if (!adminUrl) {
    return null;
  }

  return (
    <a
      href={adminUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={cn('inline-flex items-center', className)}
    >
      <img src={korintLogo} alt="Korint Admin" className="w-5 h-5" />
    </a>
  );
}
